import { useContext, useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  IconButton,
  Input,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Text,
  Tr,
  Checkbox,
  VStack,
  useToast,
  useDisclosure,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiSearch,
  FiSettings,
  FiSave,
  FiDownload,
  FiShare2,
  FiPlay,
} from "react-icons/fi";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import ShareReportModal from "../../components/ShareReportModal";
import { Loading } from "../../components/Loading";
import ValidatedInput from "../../components/ValidatedInput";
import { FormControl, FormLabel, FormErrorMessage } from "@chakra-ui/react";
import axios from "axios";
import { AuthContext } from "../../providers/AuthValidationLayer";

const API_URL = import.meta.env.VITE_API_URL;

const FILTER_CONDITIONS = [
  { value: "equals", label: "Equals" },
  { value: "notEquals", label: "Not Equals" },
  { value: "contains", label: "Contains" },
  { value: "startsWith", label: "Starts With" },
  { value: "endsWith", label: "Ends With" },
  { value: "gt", label: "Greater Than" },
  { value: "gte", label: "Greater Than or Equal" },
  { value: "lt", label: "Less Than" },
  { value: "lte", label: "Less Than or Equal" },
];

const normalizeReportPath = (path) => {
  if (typeof path !== "string") return path;
  if (path.startsWith("Lead.")) return path.replace("Lead.", "Leads.");
  if (path === "Leads.fareDetails.baseFare") return "Leads.estimatedFare";
  return path;
};

const normalizeTableForSchema = (table) => {
  if (table === "Employees") return "Admin";
  if (table === "Packages") return "Package";
  return table;
};

const normalizePathForData = (path) => {
  if (path.startsWith("Employees.")) {
    return path.replace("Employees.", "Admin.");
  }
  return path;
};




const TABLE_MAPPING = {
  Leads: { key: "Leads", endpoint: "/management/leads" },
  "Customer Management": { key: "Customer", endpoint: "/management/customers" },
  Finance: { key: "Payments", endpoint: "/finance/customers" },
  Payments: { key: "Payments", endpoint: "/finance/customers" },
  "Driver Earnings": { key: "DriverEarnings", endpoint: "/api/accounts/driver/earnings" },
  "Fleet Management": { key: "Fleet", endpoint: "/inventory/fleets" },

  Roles: { key: "Roles", endpoint: "/roles/all" },
  Employees: { key: "Employees", endpoint: "/admin/list" },
  "Audit Logs": { key: "AuditLogs", endpoint: "/auditlogs" },
  "Audit Logs Stats": { key: "AuditLogsStats", endpoint: "/auditlogs/stats" },

  "Vehicle-segment": { key: "VehicleSegment", endpoint: "/management/vehicle-segments" },
  Taxes: { key: "Taxes", endpoint: "/management/tax" },
  Customer: { key: "Customer", endpoint: "/management/customers" },
  "Custom-booking": { key: "Booking", endpoint: "/management/bookings" },
  Packages: { key: "Packages", endpoint: "/management/packages" },

  "Driver Approvals": { key: "Driver", endpoint: "/inventory/drivers" },
  "Vehicle Approvals": { key: "Fleet", endpoint: "/inventory/fleets" },
  "Vendor Approvals": { key: "Vendor", endpoint: "/inventory/vendors" },

  Complaints: { key: "Complaints", endpoint: "/api/support/tickets" },
  FAQs: { key: "FAQs", endpoint: "/support/faqs" },
  "Knowledge Base": { key: "KnowledgeBase", endpoint: "/support/kb" },
  "Training Videos": { key: "TrainingVideos", endpoint: "/support/videos" },
};

const HIDDEN_REPORT_TABLES = new Set([
  "Complaints",
  "FAQs",
  "KnowledgeBase",
  "TrainingVideos",
  "Knowledge Base",
  "Training Videos",
]);

const REPORT_TABLE_ALIASES = {
  Bookings: "Leads",
  "Customer List": "Customer",
  "Booking History": "Customer",
  "Customer Policies": "Customer",
  "Vehicle Segments": "VehicleSegment",
  "Custom Booking": "Booking",
  "Tour Package Management": "Packages",
  "State Tax List": "Taxes",
  "Finance Customer": "Customer",
  "Finance Driver": "Driver",
  "Finance Vendor": "Vendor",
  "Generate Invoice and Receipts": "Payments",
};

const toReportTableKey = (table) => REPORT_TABLE_ALIASES[table] || table;

// API endpoint mapping for each table/model


const TABLE_RELATIONS = {
  Booking: {
    leadID: { table: "Leads", localKey: "leadID", foreignKey: "_id" },
    vehicleSegment: { table: "VehicleSegment", localKey: "vehicleSegment", foreignKey: "_id" },
    fleetVehicle: { table: "Fleet", localKey: "fleetVehicle", foreignKey: "_id" },
    package: { table: "Packages", localKey: "package", foreignKey: "_id" },
    customer: { table: "Customer", localKey: "customer", foreignKey: "_id" },
    passengers: { table: "Passenger", localKey: "passengers", foreignKey: "_id", isArray: true },
    driver: { table: "Driver", localKey: "driver", foreignKey: "_id" },
    payments: { table: "Payments", localKey: "payments", foreignKey: "_id", isArray: true },
  },

  Fleet: {
    vehicleType: { table: "VehicleSegment", localKey: "vehicleType", foreignKey: "_id" },
    driverId: { table: "Driver", localKey: "ownershipDetails.driverId", foreignKey: "_id" },
    vendorId: { table: "Vendor", localKey: "ownershipDetails.vendorId", foreignKey: "_id" },
  },

  Driver: {
    bookings: { table: "Booking", localKey: "_id", foreignKey: "driver" },
    customBookings: { table: "CustomBooking", localKey: "_id", foreignKey: "driver" },
    fleets: { table: "Fleet", localKey: "_id", foreignKey: "ownershipDetails.driverId" },
    earnings: { table: "DriverEarnings", localKey: "_id", foreignKey: "driverId" },
    dispatches: { table: "Dispatch", localKey: "_id", foreignKey: "driverId" },
    rides: { table: "Ride", localKey: "_id", foreignKey: "driver" },
  },

  Vendor: {
    fleets: { table: "Fleet", localKey: "_id", foreignKey: "ownershipDetails.vendorId" },
  },

  VehicleSegment: {
    bookings: { table: "Booking", localKey: "_id", foreignKey: "vehicleSegment" },
    customBookings: { table: "CustomBooking", localKey: "_id", foreignKey: "vehicleSegment" },
    leadsByCarType: { table: "Leads", localKey: "_id", foreignKey: "carType" },
    leadsByVehicleSegment: { table: "Leads", localKey: "_id", foreignKey: "vehicleSegment" },
    fleets: { table: "Fleet", localKey: "_id", foreignKey: "vehicleType" },
  },

  Leads: {
    carType: { table: "VehicleSegment", localKey: "carType", foreignKey: "_id" },
    vehicleSegment: { table: "VehicleSegment", localKey: "vehicleSegment", foreignKey: "_id" },
    customer: { table: "Customer", localKey: "customer", foreignKey: "_id" },
    passengers: { table: "Passenger", localKey: "passengers", foreignKey: "_id", isArray: true },
  },

  Customer: {
    corporateDetails: { table: "CorporateDetails", localKey: "corporateDetails", foreignKey: "_id" },
    passengers: { table: "Passenger", localKey: "passengers", foreignKey: "_id", isArray: true },
    bookings: { table: "Booking", localKey: "_id", foreignKey: "customer" },
    leads: { table: "Leads", localKey: "_id", foreignKey: "customer" },
    payments: { table: "Payments", localKey: "_id", foreignKey: "customer" },
    customBookings: { table: "CustomBooking", localKey: "_id", foreignKey: "customer" },
    rides: { table: "Ride", localKey: "_id", foreignKey: "customer" },
  },

  Payments: {
    customer: { table: "Customer", localKey: "customer", foreignKey: "_id" },
    lead: { table: "Leads", localKey: "lead", foreignKey: "_id" },
    booking: { table: "Booking", localKey: "booking", foreignKey: "_id" },
    customBooking: { table: "CustomBooking", localKey: "customBooking", foreignKey: "_id" },
  },

  DriverEarnings: {
    driverId: { table: "Driver", localKey: "driverId", foreignKey: "_id" },
    rideId: { table: "Ride", localKey: "rideId", foreignKey: "_id" },
  },

  Dispatch: {
    bookingId: { table: "Booking", localKey: "bookingId", foreignKey: "_id" },
    driverId: { table: "Driver", localKey: "driverId", foreignKey: "_id" },
    assignedBy: { table: "Employees", localKey: "assignedBy", foreignKey: "_id" },
  },

  Ride: {
    customer: { table: "Customer", localKey: "customer", foreignKey: "_id" },
    driver: { table: "Driver", localKey: "driver", foreignKey: "_id" },
  },

  Roles: {
    parentRoleId: { table: "Roles", localKey: "parentRoleId", foreignKey: "_id" },
    reportId: { table: "Report", localKey: "reportAccess.reportId", foreignKey: "_id", isArray: true },
  },

  Employees: {
    roleId: { table: "Roles", localKey: "roleId", foreignKey: "_id" },
    parentRoleId: { table: "Roles", localKey: "parentRoleId", foreignKey: "_id" },
  },

  Report: {
    createdBy: { table: "Employees", localKey: "createdBy", foreignKey: "_id" },
    sharedBy: { table: "Employees", localKey: "sharedBy", foreignKey: "_id" },
    roleAccess: { table: "Roles", localKey: "accessControl.roleAccess.roleId", foreignKey: "_id", isArray: true },
    userAccess: { table: "Employees", localKey: "accessControl.userAccess.userId", foreignKey: "_id", isArray: true },
  },

  Complaints: {
    assignedTo: { table: "Employees", localKey: "assignedTo", foreignKey: "_id" },
    bookingId: { table: "Booking", localKey: "bookingId", foreignKey: "_id" },
    createdBy: { table: "Customer", localKey: "createdBy", foreignKey: "_id" }, // refPath model-dependent
  },

  TicketComments: {
    ticketId: { table: "Complaints", localKey: "ticketId", foreignKey: "_id" },
    commentedBy: { table: "Employees", localKey: "commentedBy", foreignKey: "_id" }, // refPath model-dependent
  },

  FAQs: {
    category: { table: "FAQCategory", localKey: "category", foreignKey: "_id" },
  },

  // no direct refs
  Packages: {},
  Taxes: {},
  AuditLogs: {
    performedBy: { table: "Employees", localKey: "performedBy", foreignKey: "_id" },
  },
};

const FILTER_CONFIG = {
  String: [
    { value: "equals", label: "Equals" },
    { value: "contains", label: "Contains" },
    { value: "startsWith", label: "Starts With" },
    { value: "endsWith", label: "Ends With" },
  ],
  Number: [
    { value: "equals", label: "Equals" },
    { value: "gt", label: "Greater Than" },
    { value: "gte", label: "Greater Than or Equal" },
    { value: "lt", label: "Less Than" },
    { value: "lte", label: "Less Than or Equal" },
  ],
  Date: [
    { value: "equals", label: "Equals" },
    { value: "lt", label: "Before" },
    { value: "gt", label: "After" },
  ],
  Boolean: [
    { value: "equals", label: "Equals" },
  ],
  Array: [
    { value: "contains", label: "Contains" },
  ],
  ObjectId: [
    { value: "equals", label: "Equals" },
  ]
};

const CreateReport = () => {
  const { roleAccess: contextRoleAccess } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  // State for table schemas fetched from API
  const [tableSchemas, setTableSchemas] = useState({});
  const [schemasLoading, setSchemasLoading] = useState(false);
  const [perPage, setPerPage] = useState("10");
  const [selectedTables, setSelectedTables] = useState([]);
  const [tableSearch, setTableSearch] = useState("");
  const [availableSearch, setAvailableSearch] = useState("");
  const [selectedSearch, setSelectedSearch] = useState("");
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [reportName, setReportName] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [reportNameError, setReportNameError] = useState("");
  const [reportDescriptionError, setReportDescriptionError] = useState("");
  const [loadingEditReport, setLoadingEditReport] = useState(false);
  const [allowedTables, setAllowedTables] = useState([]);
  const [showFilterBuilder, setShowFilterBuilder] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [filterDraft, setFilterDraft] = useState({
    columnPath: "",
    condition: "equals",
    value: "",
  });
  const toast = useToast();
  const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useDisclosure();

  const breadcrumbItems = [
    { title: "Report", link: "/stats" },
    { title: "Create Report", link: "/reports/create" },
  ];

  const fetchRoleTables = async () => {
    try {
      const roleAccess = Array.isArray(contextRoleAccess)
        ? contextRoleAccess
        : [];

      // Allow only modules with read access to appear in the report builder.
      const tables = roleAccess.flatMap((section) =>
        (section?.types || [])
          .filter((type) => {
            if (!Array.isArray(type?.access)) return false;
            return type.access.includes("GET");
          })
          .map((type) => toReportTableKey(type.subHeading))
      );

      if (tables.length > 0) {
        setAllowedTables([...new Set(tables)]);
        return;
      }

      const token = localStorage.getItem("prima_session");
      const cleanToken = token?.replace(/^Bearer\s+/i, "");

      const userStr = localStorage.getItem("prima_user");
      const user = userStr ? JSON.parse(userStr) : null;
      const roleId = user?.roleId?._id || user?.roleId || null;

      if (!roleId) {
        setAllowedTables([]);
        return;
      }

      const res = await axios.get(`${API_URL}/roles/details/${roleId}`, {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      });

      const fallbackRoleAccess = res.data?.data?.roleAccess || [];
      const fallbackTables = fallbackRoleAccess.flatMap((section) =>
        (section?.types || [])
          .filter((type) => {
            if (!Array.isArray(type?.access)) return false;
            return type.access.includes("GET");
          })
          .map((type) => toReportTableKey(type.subHeading))
      );

      setAllowedTables([...new Set(fallbackTables)]);
    } catch (error) {
      console.error("Error fetching role tables:", error);
      setAllowedTables([]);
    }
  };

  useEffect(() => {
    fetchRoleTables();
  }, []);

  // Create dynamic table endpoints based on allowed tables
  const dynamicTableEndpoints = useMemo(() => {
    const map = {};

    allowedTables.forEach((name) => {
      const reportTableKey = toReportTableKey(name);
      const mapping = TABLE_MAPPING[reportTableKey] || TABLE_MAPPING[name];

      if (HIDDEN_REPORT_TABLES.has(reportTableKey) || HIDDEN_REPORT_TABLES.has(mapping?.key)) {
        return;
      }

      if (mapping) {
        map[mapping.key] = mapping.endpoint;
      } else {
        console.warn("❌ Missing TABLE_MAPPING for:", reportTableKey);
      }
    });

    console.log("📊 Dynamic Table Endpoints:", map);

    return map;
  }, [allowedTables]);

  const fetchTableSchemas = async () => {
    try {
      setSchemasLoading(true);

      const token = localStorage.getItem("prima_session");
      const cleanToken = token?.replace(/^Bearer\s+/i, "");

      const res = await axios.get(`${API_URL}/api/schemas`, {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      });

      setTableSchemas(res.data.data);

    } catch (error) {
      console.error("Schema error:", error);
    } finally {
      setSchemasLoading(false);
    }
  };

  useEffect(() => {
    fetchTableSchemas();
  }, []);

  // Load schemas on component mount and when allowed tables change


  useEffect(() => {
    if (!isEditMode) return;

    const token = localStorage.getItem("prima_session");
    const cleanToken = token?.replace(/^Bearer\s+/i, "") || "";

    setLoadingEditReport(true);
    axios
      .get(`${API_URL}/report/${id}?_t=${Date.now()}`, {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      })
      .then((res) => {
        const report = res?.data?.data;
        if (!report) return;

        setReportName(report.reportName || "");
        setReportDescription(report.description || "");
        setSelectedTables(Array.isArray(report.tables) ? report.tables : []);
        setSelectedColumns(
          Array.isArray(report.selectedColumns)
            ? report.selectedColumns.map((col) => ({
              ...col,
            }))
            : []
        );
      })
      .catch((error) => {
        toast({
          title: "Error loading report",
          description: error.response?.data?.message || "Failed to load report for editing.",
          status: "error",
          duration: 4000,
        });
      })
      .finally(() => {
        setLoadingEditReport(false);
      });
  }, [id, isEditMode, toast]);

  const sourceTables = Object.keys(dynamicTableEndpoints);

  const availableColumns = useMemo(() => {
    if (selectedTables.length === 0) return [];

    let allColumns = [];

    selectedTables.forEach((table) => {
      const schemaCols = tableSchemas[normalizeTableForSchema(table)] || [];

      const prefixed = schemaCols.map(col => ({
        label: `${table} - ${col.label}`,
        path: `${table}.${col.path}`,
        table,
        type: col.type,
      }));
      console.log(`Columns for ${table}:`, prefixed);

      allColumns = [...allColumns, ...prefixed];
    });

    return allColumns.filter(col =>
      !selectedColumns.find(sc => sc.path === col.path) &&
      col.label.toLowerCase().includes(availableSearch.toLowerCase())
    );

  }, [selectedTables, selectedColumns, availableSearch, tableSchemas]);

  const filteredSelectedColumns = useMemo(() => {
    return selectedColumns.filter(col =>
      col.label.toLowerCase().includes(selectedSearch.toLowerCase())
    );
  }, [selectedColumns, selectedSearch]);

  const filterableColumns = useMemo(() => {
    if (selectedTables.length === 0) return [];

    let allColumns = [];

    selectedTables.forEach((table) => {
      const schemaCols = tableSchemas[normalizeTableForSchema(table)] || [];
      const prefixed = schemaCols.map((col) => ({
        label: `${table} - ${col.label}`,
        path: `${table}.${col.path}`,
      }));
      allColumns = [...allColumns, ...prefixed];
    });

    return allColumns;
  }, [selectedTables, tableSchemas]);

  const selectedColumnType = useMemo(() => {
    const col =
      selectedColumns.find(c => c.path === filterDraft.columnPath) ||
      filterableColumns.find(c => c.path === filterDraft.columnPath);

    return col?.type || "String";
  }, [filterDraft.columnPath, selectedColumns, filterableColumns]);

  const buildAutoJoins = () => {
    const joins = [];
    const seen = new Set();

    selectedColumns.forEach((column) => {
      if (!column?.path) return;

      const pathParts = column.path.split(".");
      if (pathParts.length < 2) return;

      const tableName = pathParts[0];
      const relationField = pathParts[1];
      const relatedTable = TABLE_RELATIONS?.[tableName]?.[relationField];

      if (!relatedTable) return;

      const key = `${tableName}.${relationField}`;
      if (seen.has(key)) return;

      joins.push({
        fromTable: tableName,
        localField: relationField,
        toTable: relatedTable,
      });
      seen.add(key);
    });

    return joins;
  };

  const handleColumnSelect = (column) => {
    if (!selectedColumns.find(c => c.path === column.path)) {
      setSelectedColumns([...selectedColumns, column]);
    }
  };

  const handleColumnDeselect = (column) => {
    setSelectedColumns(selectedColumns.filter(c => c.path !== column.path));
  };

  const addFilter = () => {
    const { columnPath, condition, value } = filterDraft;
    if (!columnPath || !condition || !String(value).trim()) {
      toast({
        title: "Incomplete filter",
        description: "Choose column, condition and value before applying.",
        status: "warning",
        duration: 2500,
      });
      return;
    }

    const normalizedPath = normalizeReportPath(columnPath);
    const chosenColumn = selectedColumns.find((col) => col.path === normalizedPath);
    setAppliedFilters((prev) => [
      ...prev,
      {
        id: Date.now(),
        field: normalizedPath,
        label: chosenColumn?.label || normalizedPath,
        condition,
        value: String(value).trim(),
      },
    ]);

    setFilterDraft({
      columnPath: "",
      condition: "equals",
      value: "",
    });
  };

  const removeFilter = (id) => {
    setAppliedFilters((prev) => prev.filter((filter) => filter.id !== id));
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => {
      if (Array.isArray(current)) {
        return current[0]?.[key]; // 👈 take first element
      }
      return current?.[key];
    }, obj);
  };

  // Format complex values recursively
  const formatValue = (value, depth = 0) => {
    if (value === null || value === undefined) {
      return "-";
    }

    // Dates
    if (value instanceof Date || typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      try {
        return new Date(value).toLocaleDateString();
      } catch {
        return String(value);
      }
    }

    // Booleans
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    // Arrays
    if (Array.isArray(value)) {
      if (value.length === 0) return "Empty";
      // If array of primitives
      if (typeof value[0] !== "object") {
        return value.slice(0, 3).join(", ") + (value.length > 3 ? `...+${value.length - 3}` : "");
      }
      // If array of objects
      return `${value.length} items`;
    }

    // Objects
    if (typeof value === "object") {
      const entries = Object.entries(value)
        .filter(([k, v]) => v !== null && v !== undefined && v !== "")
        .map(([k, v]) => {
          const key = k.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase()).trim();

          // For nested objects, show count instead of recursing
          if (typeof v === "object" && !Array.isArray(v) && !(v instanceof Date)) {
            return `${key}: Object`;
          }

          // For arrays in nested object
          if (Array.isArray(v)) {
            return `${key}: ${v.length} items`;
          }

          // For primitives, truncate long strings
          const val = String(v).length > 20 ? String(v).substring(0, 20) + "..." : String(v);
          return `${key}: ${val}`;
        })
        .slice(0, 4); // Show max 4 key-value pairs

      return entries.length === 0 ? "Object" : entries.join(", ");
    }

    // Primitives - truncate if too long
    const str = String(value);
    return str.length > 50 ? str.substring(0, 50) + "..." : str;
  };

  const fetchReportData = async () => {
    if (!selectedTables || selectedTables.length === 0) {
      toast({
        title: "No table selected",
        description: "Please select a table first.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (selectedColumns.length === 0) {
      toast({
        title: "No columns selected",
        description: "Please select at least one column.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("prima_session");
      const cleanToken = token?.replace(/^Bearer\s+/i, "").replace(/['"]/g, "");
      const autoJoins = buildAutoJoins();
      const filtersPayload = appliedFilters.map((filter) => ({
        field: filter.field,
        condition: filter.condition,
        value: filter.value,
      }));

      const response = await axios.post(`${API_URL}/report/run`, {
        tables: selectedTables,
        selectedColumns: selectedColumns.map((col) => ({
          ...col,
          path: normalizeReportPath(col.path),
        })),
        joins: autoJoins,
        filters: filtersPayload,
      }, {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      });

      if (response.data) {
        const data = Array.isArray(response.data.data) ? response.data.data : (Array.isArray(response.data) ? response.data : []);
        setReportData(data || []);
        setTotalRecords(data.length || 0);
        setCurrentPage(1);
        toast({
          title: "Report generated",
          description: `Loaded ${data.length || 0} records.`,
          status: "success",
          duration: 2000,
        });
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to fetch report data.",
        status: "error",
        duration: 4000,
      });
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const saveReport = async () => {
    // Validate Report Name
    if (!reportName.trim()) {
      setReportNameError("Report name is required");
      toast({
        title: "Report name required",
        description: "Please enter a report name before saving.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (reportName.trim().length < 2) {
      setReportNameError("Report name must be at least 2 characters");
      toast({
        title: "Invalid report name",
        description: "Report name must be at least 2 characters.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (reportName.length > 100) {
      setReportNameError("Report name cannot exceed 100 characters");
      toast({
        title: "Report name too long",
        description: "Report name cannot exceed 100 characters.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (reportDescription.length > 500) {
      setReportDescriptionError("Description cannot exceed 500 characters");
      toast({
        title: "Description too long",
        description: "Description cannot exceed 500 characters.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (!selectedTables || selectedTables.length === 0) {
      toast({
        title: "No table selected",
        description: "Please select a table before saving.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (selectedColumns.length === 0) {
      toast({
        title: "No columns selected",
        description: "Please select columns before saving.",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    try {
      setSavingReport(true);
      const token = localStorage.getItem("prima_session");
      const cleanToken = token?.replace(/^Bearer\s+/i, "") || "";

      // Send report metadata matching backend schema
      const reportPayload = {
        reportName: reportName,
        description: reportDescription,
        tables: selectedTables,
        selectedColumns: selectedColumns.map(col => ({
          label: col.label,
          path: col.path,
        })),
      };

      console.log("Sending report payload:", reportPayload);

      const response = isEditMode
        ? await axios.put(`${API_URL}/report/${id}`, reportPayload, {
          headers: {
            Authorization: `Bearer ${cleanToken}`,
            "Content-Type": "application/json",
          },
        })
        : await axios.post(`${API_URL}/report/add`, reportPayload, {
          headers: {
            Authorization: `Bearer ${cleanToken}`,
            "Content-Type": "application/json",
          },
        });

      if (response.data.success || response.status === 201 || response.status === 200) {
        toast({
          title: isEditMode ? "Report updated successfully!" : "Report saved successfully!",
          description: isEditMode
            ? `Report "${reportName}" has been updated.`
            : `Report "${reportName}" has been saved.`,
          status: "success",
          duration: 3000,
        });

        // Redirect to View Reports after successful save
        setTimeout(() => {
          navigate("/reports/view");
        }, 1500);
      }
    } catch (error) {
      console.error("Error saving report:", error);
      console.log("Response Data:", error.response?.data); // Log the actual error message
      console.log("Full Error:", JSON.stringify(error.response?.data, null, 2));
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Failed to save report.";
      toast({
        title: "Error saving report",
        description: errorMsg,
        status: "error",
        duration: 4000,
      });
    } finally {
      setSavingReport(false);
    }
  };

  const handleShareReport = async (data) => {
    try {
      if (!isEditMode || !id) {
        toast({
          title: "Save report first",
          description: "Please save this report before sharing it.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const token = localStorage.getItem("prima_session");
      const cleanToken = token?.replace(/^Bearer\s+/i, "");

      await axios.put(`${API_URL}/report/${id}/share`, data, {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      });

      toast({
        title: "Report shared successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Share error:", error);
      toast({
        title: "Error sharing report",
        description: error.response?.data?.message || "Failed to share report.",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / parseInt(perPage, 10)));
  const paginatedReportData = useMemo(() => {
    const pageSize = parseInt(perPage, 10);
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return reportData.slice(start, end);
  }, [reportData, currentPage, perPage]);
  const showingCount = paginatedReportData.length;

  const tableHeaders = selectedColumns.length > 0
    ? ["Sr. No", ...selectedColumns.map(c => c.label)]
    : ["Sr. No", "Name", "Mobile No.", "Date", "Price", "Pick Up", "Destination", "Car", "Status"];

  // Show loading state while fetching schemas
  if (schemasLoading || loadingEditReport) {
    return <Loading />;
  }

  return (
    <Box p={{ base: 4, md: 6 }} bg="#F7F9FB" minH="100vh">
      <BreadCrumbMaker items={breadcrumbItems} />

      <Flex justify="flex-end" gap={3} mt={2} mb={5}>
        <Button
          leftIcon={<FiSave />}
          variant="outline"
          colorScheme="blue"
          size="sm"
          onClick={saveReport}
          isLoading={savingReport}
          loadingText={isEditMode ? "Updating" : "Saving"}
        >
          {isEditMode ? "Update" : "Save"}
        </Button>
        <Button
          leftIcon={<FiDownload />}
          variant="outline"
          colorScheme="blue"
          size="sm"
        >
          Export
        </Button>
        <Button
          leftIcon={<FiShare2 />}
          variant="outline"
          colorScheme="blue"
          size="sm"
          onClick={onShareOpen}
        >
          Share
        </Button>
        <Button
          leftIcon={<FiPlay />}
          bg="#3E6AE1"
          color="white"
          _hover={{ bg: "#355fd0" }}
          size="sm"
          onClick={fetchReportData}
          isLoading={loading}
          loadingText="Loading"
        >
          Run
        </Button>
      </Flex>

      <Box bg="white" border="1px solid" borderColor="#D9E0F0" borderRadius="12px" p={4} mb={7}>
        <Grid templateColumns={{ base: "1fr", md: "1fr 2fr" }} gap={6} mb={4}>
          <FormControl isInvalid={!!reportNameError}>
            <FormLabel mb={2} fontWeight="600" color="#303A6D">Report Name</FormLabel>
            <ValidatedInput
              name="reportName"
              fieldType="text"
              placeholder="Enter Report Name"
              value={reportName}
              onChange={(e) => {
                let newValue = e.target.value;
                setReportName(newValue);
                
                // Validate
                const trimmed = newValue.trim();
                if (trimmed.length === 0) {
                  setReportNameError("Report name is required");
                } else if (trimmed.length < 2) {
                  setReportNameError("Report name must be at least 2 characters");
                } else if (newValue.length > 100) {
                  setReportNameError("Report name cannot exceed 100 characters");
                } else if (/^\s/.test(newValue)) {
                  setReportNameError("Report name cannot start with spaces");
                } else {
                  setReportNameError("");
                }
              }}
              maxLength={100}
              helperText={`${reportName.length}/100`}
            />
            {reportNameError && <FormErrorMessage>{reportNameError}</FormErrorMessage>}
          </FormControl>

          <FormControl isInvalid={!!reportDescriptionError}>
            <FormLabel mb={2} fontWeight="600" color="#303A6D">Report Description</FormLabel>
            <ValidatedInput
              name="reportDescription"
              fieldType="bio"
              placeholder="Enter Report Description (optional)"
              value={reportDescription}
              onChange={(e) => {
                const newValue = e.target.value;
                setReportDescription(newValue);
                if (newValue.length > 500) {
                  setReportDescriptionError("Description cannot exceed 500 characters");
                } else {
                  setReportDescriptionError("");
                }
              }}
              maxLength={500}
              helperText={`${reportDescription.length}/500`}
            />
            {reportDescriptionError && <FormErrorMessage>{reportDescriptionError}</FormErrorMessage>}
          </FormControl>
        </Grid>

        <Text fontWeight="700" color="#303A6D" mb={3}>Select Table</Text>
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr 1fr" }} gap={5} alignItems="start">
          <Box>
            <Input
              mb={3}
              placeholder="Search tables..."
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              size="sm"
              bg="white"
              borderColor="#E3E8F5"
              _placeholder={{ color: "#7A869A" }}
            />
            <Box bg="#F8FAFD" border="1px solid" borderColor="#E3E8F5" borderRadius="8px" p={3} h="200px" overflowY="auto">
              <VStack align="stretch" spacing={1}>
                {sourceTables
                  .filter((table) =>
                    table.toLowerCase().includes(tableSearch.toLowerCase())
                  )
                  .map((table) => (
                    <Checkbox
                      key={table}
                      isChecked={selectedTables.includes(table)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTables((prev) => [...prev, table]);
                        } else {
                          setSelectedTables((prev) =>
                            prev.filter((t) => t !== table)
                          );
                        }
                        setSelectedColumns([]);
                      }}
                    >
                      {table}
                    </Checkbox>
                  ))}
              </VStack>
            </Box>
            {selectedTables.length > 0 && (
              <Text fontSize="sm" mt={2} color="gray.600">
                Selected: {selectedTables.join(", ")}
              </Text>
            )}

            <Button
              mt={3}
              size="sm"
              w="full"
              variant="outline"
              onClick={() => setShowFilterBuilder((prev) => !prev)}
            >
              {showFilterBuilder ? "Hide Filters" : "Apply Filter"}
            </Button>

            {showFilterBuilder && (
              <Box mt={3} p={3} bg="#F8FAFD" border="1px solid" borderColor="#E3E8F5" borderRadius="8px">
                <VStack spacing={2} align="stretch">
                  <Select
                    size="sm"
                    placeholder="Choose Column"
                    value={filterDraft.columnPath}
                    onChange={(e) =>
                      setFilterDraft((prev) => ({ ...prev, columnPath: e.target.value }))
                    }
                  >
                    {selectedColumns.map((col) => (
                      <option key={col.path} value={col.path}>
                        {col.label}
                      </option>
                    ))}
                  </Select>

                  <Select
                    size="sm"
                    value={filterDraft.condition}
                    onChange={(e) =>
                      setFilterDraft((prev) => ({ ...prev, condition: e.target.value }))
                    }
                  >
                    {(FILTER_CONFIG[selectedColumnType] || []).map((condition) => (
                      <option key={condition.value} value={condition.value}>
                        {condition.label}
                      </option>
                    ))}
                  </Select>

                  {selectedColumnType === "Number" && (
                    <Input
                      type="number"
                      size="sm"
                      value={filterDraft.value}
                      onChange={(e) =>
                        setFilterDraft((prev) => ({ ...prev, value: e.target.value }))
                      }
                    />
                  )}

                  {selectedColumnType === "Date" && (
                    <Input
                      type="date"
                      size="sm"
                      value={filterDraft.value}
                      onChange={(e) =>
                        setFilterDraft((prev) => ({ ...prev, value: e.target.value }))
                      }
                    />
                  )}

                  {selectedColumnType === "Boolean" && (
                    <Select
                      size="sm"
                      value={filterDraft.value}
                      onChange={(e) =>
                        setFilterDraft((prev) => ({ ...prev, value: e.target.value }))
                      }
                    >
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </Select>
                  )}

                  {["String", "ObjectId", "Array"].includes(selectedColumnType) && (
                    <Input
                      size="sm"
                      placeholder="Enter value"
                      value={filterDraft.value}
                      onChange={(e) =>
                        setFilterDraft((prev) => ({ ...prev, value: e.target.value }))
                      }
                    />
                  )}

                  <Flex gap={2}>
                    <Button size="sm" colorScheme="blue" onClick={addFilter}>
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setAppliedFilters([]);
                        setFilterDraft({ columnPath: "", condition: "equals", value: "" });
                      }}
                    >
                      Clear
                    </Button>
                  </Flex>

                  {appliedFilters.length > 0 && (
                    <VStack align="stretch" spacing={1} mt={1}>
                      {appliedFilters.map((filter) => (
                        <Flex key={filter.id} justify="space-between" align="center" bg="white" p={2} borderRadius="6px">
                          <Text fontSize="xs" color="#303A6D">
                            {filter.label} = [{filter.condition}] = {filter.value}
                          </Text>
                          <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeFilter(filter.id)}>
                            x
                          </Button>
                        </Flex>
                      ))}
                    </VStack>
                  )}
                </VStack>
              </Box>
            )}
          </Box>

          <Box>
            <Text mb={2} fontWeight="600" color="#303A6D">Available Columns</Text>
            <Input
              mb={3}
              placeholder="Search columns"
              size="sm"
              value={availableSearch}
              onChange={(e) => setAvailableSearch(e.target.value)}
            />
            <Box bg="#F8FAFD" border="1px solid" borderColor="#E3E8F5" borderRadius="8px" p={3} h="200px" overflowY="auto">
              {selectedTables.length === 0 ? (
                <Text fontSize="sm" color="gray.500">Select a table first</Text>
              ) : availableColumns.length === 0 ? (
                <Text fontSize="sm" color="gray.500">All columns selected</Text>
              ) : (
                <VStack align="stretch" spacing={1}>
                  {availableColumns.map((col) => (
                    <Flex
                      key={col.path}
                      p={2}
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: "#E8F0FF" }}
                      onClick={() => handleColumnSelect(col)}
                      justify="space-between"
                      align="center"
                    >
                      <Text color="#303A6D" fontSize="sm">{col.label}</Text>
                      <Button size="xs" colorScheme="blue" variant="ghost">+</Button>
                    </Flex>
                  ))}
                </VStack>
              )}
            </Box>
          </Box>

          <Box>
            <Text mb={2} fontWeight="600" color="#303A6D">Selected Columns</Text>
            <Input
              mb={3}
              placeholder="Search selected"
              size="sm"
              value={selectedSearch}
              onChange={(e) => setSelectedSearch(e.target.value)}
            />
            <Box bg="#F8FAFD" border="1px solid" borderColor="#E3E8F5" borderRadius="8px" p={3} h="200px" overflowY="auto">
              {filteredSelectedColumns.length === 0 ? (
                <Text fontSize="sm" color="gray.500">No columns selected</Text>
              ) : (
                <VStack align="stretch" spacing={1}>
                  {filteredSelectedColumns.map((col) => (
                    <Flex
                      key={col.path}
                      p={2}
                      borderRadius="md"
                      bg="#E8F0FF"
                      justify="space-between"
                      align="center"
                    >
                      <Text color="#303A6D" fontSize="sm">{col.label}</Text>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => handleColumnDeselect(col)}
                      >
                        ×
                      </Button>
                    </Flex>
                  ))}
                </VStack>
              )}
            </Box>
          </Box>
        </Grid>
      </Box>

      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="lg" color="#303A6D">
          {selectedTables ? `${selectedTables} List` : "Preview Data"}
        </Heading>
        <Flex gap={3}>
          <Button rightIcon={<FiChevronUp />} variant="outline" bg="white">Action</Button>
          <IconButton aria-label="settings" icon={<FiSettings />} variant="outline" bg="white" />
        </Flex>
      </Flex>

      <Box bg="white" border="1px solid" borderColor="#D9E0F0" borderRadius="16px" overflow="hidden">
        <TableContainer>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th w="50px">
                  <Input type="checkbox" p={0} h="20px" border="1px solid" borderColor="#CBD5E0" />
                </Th>
                {tableHeaders.map((header) => (
                  <Th key={header} color="#6A7DAE">{header}</Th>
                ))}
              </Tr>
              <Tr bg="#E8F0FF">
                <Th></Th>
                {tableHeaders.map((header) => (
                  <Th key={`${header}-search`}>
                    <Input size="sm" placeholder="Search" bg="white" />
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={tableHeaders.length + 1} py={12} textAlign="center">
                    <Spinner size="lg" color="#3E6AE1" />
                  </Td>
                </Tr>
              ) : paginatedReportData.length > 0 ? (
                paginatedReportData.map((row, index) => (
                  <Tr key={row._id || index} _hover={{ bg: "#F7FAFC" }}>
                    <Td>
                      <Input type="checkbox" p={0} h="20px" border="1px solid" borderColor="#CBD5E0" />
                    </Td>
                    <Td>{(currentPage - 1) * parseInt(perPage) + index + 1}</Td>
                    {selectedColumns.map((col) => {
                      let value;

                      // ✅ ALWAYS use full path
                      value = getNestedValue(row, normalizePathForData(col.path));

                      // 3. Fallback for joined tables (Fleet, Customer, etc.)
                      if (value === undefined && col.path.includes(".")) {
                        const normalizedPath = normalizePathForData(col.path);
                        value = getNestedValue(row, normalizedPath);
                      }

                      // 4. Final fallback
                      if (value === undefined) value = null;
                      const displayValue = formatValue(value);
                      const isLong = displayValue.length > 40;

                      return (
                        <Td key={col.path} maxW="200px" whiteSpace="normal" wordBreak="break-word">
                          {isLong ? (
                            <Tooltip label={displayValue} hasArrow placement="top">
                              <Text fontSize="sm" color="#303A6D">
                                {displayValue.substring(0, 40)}...
                              </Text>
                            </Tooltip>
                          ) : (
                            <Text fontSize="sm" color="#303A6D">{displayValue}</Text>
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={tableHeaders.length + 1} py={12} textAlign="center" color="gray.500">
                    {!selectedTables
                      ? "Please select a table to view data."
                      : selectedColumns.length === 0
                        ? "Please select columns to display data."
                        : "No data available. Click 'Run' to fetch data."}
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>

        <Flex justify="center" align="center" p={4} borderTop="1px solid" borderColor="#E2E8F0">
          <Flex align="center" gap={3} color="#7A869A" fontWeight="500">
            <Text>Showing</Text>
            <Select
              w="80px"
              bg="white"
              value={perPage}
              onChange={(e) => {
                setPerPage(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </Select>
            <Text>{showingCount} of {totalRecords}</Text>
          </Flex>

          <Flex align="center" gap={1} ml={10}>
            <IconButton
              aria-label="previous"
              icon={<FiChevronLeft />}
              size="sm"
              isDisabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(currentPage - 1);
              }}
            />
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const pageNum = i + 1;
              return (
                <Button
                  key={pageNum}
                  size="sm"
                  bg={currentPage === pageNum ? "#3E6AE1" : "white"}
                  color={currentPage === pageNum ? "white" : "#303A6D"}
                  _hover={{ bg: currentPage === pageNum ? "#355fd0" : "#F7FAFC" }}
                  variant={currentPage === pageNum ? "solid" : "outline"}
                  onClick={() => {
                    setCurrentPage(pageNum);
                  }}
                >
                  {pageNum}
                </Button>
              );
            })}
            <IconButton
              aria-label="next"
              icon={<FiChevronRight />}
              size="sm"
              isDisabled={currentPage >= totalPages}
              onClick={() => {
                setCurrentPage(currentPage + 1);
              }}
            />
          </Flex>
        </Flex>
      </Box>

      <ShareReportModal
        isOpen={isShareOpen}
        onClose={onShareClose}
        reportName={reportName}
        selectedTables={selectedTables}
        selectedColumns={selectedColumns}
        onShare={handleShareReport}
      />
    </Box>
  );
};

export default CreateReport;
